// src/components/ComparisonConfigModal.jsx
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { adcoMethodLabel, isAdcoGeneratedVector } from '../utils/adcoDisplayUtils';

// Mirrors _MAX_SHOCK_BPS in users/pam/jr/trade/comparison.py — the server
// rejects anything larger, so don't let the form submit it.
const MAX_SHOCK_BPS = 1000;

// Deal-assumption fields come from the server's column_mapping.json (via
// /form/security/fields) — the single source for labels, choices, and help.
// scenario_group / scenario_placeholder are its presentation hints for this
// editor: a field with a scenario_group appears here. Display-name keys are
// intentional: the selected deal arrives with frontend column labels, and
// these keys are passed to the comparison API unchanged.
const scenarioParamsFromFields = formFields => (formFields || [])
  .filter(field => field.scenario_group)
  .map(field => ({
    key: field.label,
    label: field.label,
    type: (field.choices || []).length ? 'select' : 'text',
    choices: field.choices || [],
    placeholder: field.scenario_placeholder || '',
    group: field.scenario_group,
    help: field.help || '',
  }));

// Preferred group display order; unknown groups from the server render after.
const GROUP_ORDER = ['Prepayment', 'Default / loss', 'Draw', 'Other'];

// "Run to call" rides on the existing deal-override mechanism: overriding
// Option Redemption Value with ASAP makes the server exercise the deal's
// optional redemption at its first allowed date, so yield and WAL price to
// call. Overrides cannot blank a field, so the deal's own redemption name (if
// any) stays and that named call is the one exercised.
const RUN_TO_CALL_KEY = 'Option Redemption Value';
const RUN_TO_CALL_VALUE = 'ASAP';
const RUN_TO_CALL_HELP = "Exercise the deal's optional redemption at the first allowed date so yield and WAL price to call.";
const hasValue = value => value !== '' && value !== null && value !== undefined;

const ComparisonConfigModal = memo(({
  open,
  formFields = [],
  onClose,
  onSave,
  availableTranches = [],
  selectedTranches = null,
  existingRun = null,
  existingLabels = [],
  dealData = null,
  runSource = 'custom',
  curveScenarioOptions = [],
  curveAsofdt = null,
  defaultLabel = '',
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [label, setLabel] = useState('');
  const [localPrices, setLocalPrices] = useState({});
  const [localYields, setLocalYields] = useState({});
  const [localSpreads, setLocalSpreads] = useState({});
  const [dealOverrides, setDealOverrides] = useState({});
  const [curveScenario, setCurveScenario] = useState('');
  const [shockBps, setShockBps] = useState('');
  // ADCO runs only: ADCO Cashflow derives CPR/CDR/severity from summed loan cashflow dollars.
  const [adcoCshf, setAdcoCshf] = useState(false);
  const source = existingRun?.source || runSource;
  const isAdcoRun = source === 'adco';
  // A run prices off one curve: either a named scenario or a parallel shock.
  const hasShock = shockBps !== '' && Number(shockBps) !== 0;
  const hasCurveScenario = Boolean(curveScenario);
  const shockOutOfRange = hasShock && Math.abs(Number(shockBps)) > MAX_SHOCK_BPS;

  const scenarioParams = useMemo(() => scenarioParamsFromFields(formFields), [formFields]);
  const paramGroups = useMemo(() => {
    const groups = [...new Set(scenarioParams.map(param => param.group))];
    return [
      ...GROUP_ORDER.filter(group => groups.includes(group)),
      ...groups.filter(group => !GROUP_ORDER.includes(group)),
    ];
  }, [scenarioParams]);

  const baseValues = useMemo(() => {
    if (!dealData) return {};
    return Object.fromEntries(
      scenarioParams
        .filter(param => hasValue(dealData[param.key]))
        .map(param => [param.key, String(dealData[param.key])])
    );
  }, [dealData, scenarioParams]);

  useEffect(() => {
    if (!open) return;

    setActiveTab(0);
    if (!existingRun) {
      setLabel(defaultLabel);
      setLocalPrices({});
      setLocalYields({});
      setLocalSpreads({});
      setDealOverrides({});
      setCurveScenario('');
      setShockBps('');
      setAdcoCshf(false);
      return;
    }

    const prices = {};
    const yields = {};
    const spreads = {};
    Object.entries(existingRun.tranche_target || {}).forEach(([tranche, target]) => {
      if (hasValue(target?.price)) prices[tranche] = target.price;
      if (hasValue(target?.yield)) yields[tranche] = target.yield;
      if (hasValue(target?.spread)) spreads[tranche] = target.spread;
    });

    setLabel(existingRun.label || '');
    setLocalPrices(prices);
    setLocalYields(yields);
    setLocalSpreads(spreads);
    setDealOverrides(existingRun.deal_overrides || {});
    setCurveScenario(existingRun.curve_scenario || '');
    setShockBps(
      existingRun.shock_bps === 0 || existingRun.shock_bps == null
        ? ''
        : String(existingRun.shock_bps)
    );
    setAdcoCshf(Boolean(existingRun.adco_cshf));
  }, [defaultLabel, existingRun, open]);

  const visibleTranches = selectedTranches ?? availableTranches;
  const normalizedLabel = label.trim().toLowerCase();
  const originalLabel = (existingRun?.label || '').trim().toLowerCase();
  const duplicateLabel = Boolean(normalizedLabel && existingLabels.some(existingLabel => {
    const normalizedExisting = String(existingLabel).trim().toLowerCase();
    return normalizedExisting === normalizedLabel && normalizedExisting !== originalLabel;
  }));

  const overrideCount = Object.keys(dealOverrides).length;
  const runToCall = dealOverrides[RUN_TO_CALL_KEY] === RUN_TO_CALL_VALUE;
  const targetCount = visibleTranches.filter(tranche => (
    hasValue(localPrices[tranche])
    || hasValue(localYields[tranche])
    || hasValue(localSpreads[tranche])
  )).length;

  const isPriceDisabled = tranche => hasValue(localYields[tranche]) || hasValue(localSpreads[tranche]);
  const isYieldDisabled = tranche => hasValue(localPrices[tranche]) || hasValue(localSpreads[tranche]);
  const isSpreadDisabled = tranche => hasValue(localPrices[tranche]) || hasValue(localYields[tranche]);

  const handleInputChange = useCallback((setter, tranche, value) => {
    const numericValue = value === '' ? '' : Number(value);
    setter(previous => ({ ...previous, [tranche]: numericValue }));
  }, []);

  const handleDealOverrideChange = useCallback((key, value) => {
    setDealOverrides(previous => {
      const next = { ...previous };
      if (!hasValue(value)) delete next[key];
      else next[key] = value;
      return next;
    });
  }, []);

  const handleSave = () => {
    if (!label.trim() || duplicateLabel) return;

    const trancheTarget = Object.fromEntries(visibleTranches.map(tranche => {
      if (hasValue(localPrices[tranche])) return [tranche, { price: localPrices[tranche] }];
      if (hasValue(localYields[tranche])) return [tranche, { yield: localYields[tranche] }];
      if (hasValue(localSpreads[tranche])) return [tranche, { spread: localSpreads[tranche] }];
      return [tranche, {}];
    }));

    onSave({
      label: label.trim(),
      tranche_target: trancheTarget,
      deal_overrides: dealOverrides,
      adco: null,
      shock_bps: shockBps === '' ? 0 : Number(shockBps),
      curve_scenario: curveScenario || null,
      curve_asofdt: curveAsofdt || existingRun?.curve_asofdt || null,
      adco_cshf: isAdcoRun && adcoCshf,
    });
  };

  const fieldSx = isOverridden => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: isOverridden
        ? alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.13 : 0.06)
        : 'background.paper',
      fontSize: '13px',
      '& fieldset': isOverridden ? { borderColor: alpha(theme.palette.warning.main, 0.52) } : {},
    },
    '& .MuiInputLabel-root': { fontSize: '13px' },
  });

  const targetFieldSx = disabled => ({
    minWidth: 110,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: disabled ? alpha(theme.palette.action.disabled, 0.05) : 'background.paper',
      fontSize: '13px',
      '& input': { textAlign: 'right', py: 0.9 },
    },
  });

  const pricingTargetsSection = (
          <Stack spacing={2}>
            <Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, color: 'text.primary' }}>
                Tranche pricing targets
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Set one target per tranche. Entering price, yield, or spread disables the other two for that tranche.
              </Typography>
            </Box>

            {visibleTranches.length === 0 ? (
              <Paper variant="outlined" sx={{ borderRadius: 2.5, p: 4, textAlign: 'center', borderStyle: 'dashed' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>No tranches selected</Typography>
                <Typography variant="body2" color="text.secondary">
                  Close this editor and add at least one tranche to the run scope.
                </Typography>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ borderRadius: 2.5, overflowX: 'auto' }}>
                <Box
                  sx={{
                    minWidth: Math.max(620, 170 + visibleTranches.length * 128),
                    display: 'grid',
                    gridTemplateColumns: `160px repeat(${visibleTranches.length}, minmax(112px, 1fr))`,
                    gap: 1,
                    alignItems: 'center',
                    p: 2,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">Target type</Typography>
                  {visibleTranches.map(tranche => (
                    <Chip key={tranche} label={tranche} size="small" variant="outlined" sx={{ justifySelf: 'stretch' }} />
                  ))}

                  <Typography sx={{ fontSize: '13px', fontWeight: 650 }}>Price</Typography>
                  {visibleTranches.map(tranche => (
                    <TextField
                      key={`price-${tranche}`}
                      placeholder="100.00"
                      type="number"
                      size="small"
                      disabled={isPriceDisabled(tranche)}
                      value={localPrices[tranche] ?? ''}
                      onChange={event => handleInputChange(setLocalPrices, tranche, event.target.value)}
                      sx={targetFieldSx(isPriceDisabled(tranche))}
                      slotProps={{ htmlInput: { 'aria-label': `${tranche} price target` } }}
                    />
                  ))}

                  <Typography sx={{ fontSize: '13px', fontWeight: 650 }}>Yield %</Typography>
                  {visibleTranches.map(tranche => (
                    <TextField
                      key={`yield-${tranche}`}
                      placeholder="5.00"
                      type="number"
                      size="small"
                      disabled={isYieldDisabled(tranche)}
                      value={localYields[tranche] ?? ''}
                      onChange={event => handleInputChange(setLocalYields, tranche, event.target.value)}
                      sx={targetFieldSx(isYieldDisabled(tranche))}
                      slotProps={{ htmlInput: { 'aria-label': `${tranche} yield target` } }}
                    />
                  ))}

                  <Typography sx={{ fontSize: '13px', fontWeight: 650 }}>Spread (bps)</Typography>
                  {visibleTranches.map(tranche => (
                    <TextField
                      key={`spread-${tranche}`}
                      placeholder="150"
                      type="number"
                      size="small"
                      disabled={isSpreadDisabled(tranche)}
                      value={localSpreads[tranche] ?? ''}
                      onChange={event => handleInputChange(setLocalSpreads, tranche, event.target.value)}
                      sx={targetFieldSx(isSpreadDisabled(tranche))}
                      slotProps={{ htmlInput: { 'aria-label': `${tranche} spread target` } }}
                    />
                  ))}
                </Box>
              </Paper>
            )}
          </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh', overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ px: { xs: 2, md: 3 }, py: 2.25 }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            >
              <TuneOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '17px', fontWeight: 750, color: 'text.primary' }}>
                {existingRun
                  ? `Edit ${isAdcoRun ? 'ADCO' : 'comparison'} run`
                  : `Add ${isAdcoRun ? 'ADCO' : 'comparison'} run`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isAdcoRun
                  ? 'Choose the ADCO method and the curve or rate shock for this run.'
                  : 'Define only what changes from the selected deal.'}
              </Typography>
            </Box>
          </Stack>
          <IconButton aria-label="Close run editor" size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />

      <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.25 }}>
        <Stack spacing={1.5}>
          <TextField
            label="Run label"
            fullWidth
            autoFocus
            value={label}
            onChange={event => setLabel(event.target.value)}
            placeholder={isAdcoRun ? 'e.g. ADCO Down 100' : 'e.g. Faster prepays'}
            error={duplicateLabel}
            helperText={duplicateLabel ? 'Run labels must be unique.' : 'Use a short label that will remain clear in tables and charts.'}
            size="small"
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <FormControl fullWidth size="small" disabled={hasShock}>
              <InputLabel id="comparison-curve-scenario-label">Curve scenario</InputLabel>
              <Select
                labelId="comparison-curve-scenario-label"
                label="Curve scenario"
                value={curveScenario}
                onChange={event => {
                  setCurveScenario(event.target.value);
                  if (event.target.value) setShockBps('');
                }}
              >
                <MenuItem value="">Base / current curve</MenuItem>
                {curveScenarioOptions.map(option => (
                  <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                {hasShock
                  ? 'Cleared while a rate shock is set'
                  : `Curve snapshot: ${curveAsofdt || 'latest on/before scenario ASOFDT'}`}
              </Typography>
            </FormControl>
            <TextField
              label="Rate shock (bps)"
              type="number"
              size="small"
              sx={{ minWidth: { sm: 190 } }}
              value={shockBps}
              disabled={hasCurveScenario}
              onChange={event => {
                setShockBps(event.target.value);
                if (event.target.value && Number(event.target.value) !== 0) setCurveScenario('');
              }}
              error={shockOutOfRange}
              inputProps={{ step: 25, min: -MAX_SHOCK_BPS, max: MAX_SHOCK_BPS }}
              helperText={
                shockOutOfRange
                  ? `Must be within +/-${MAX_SHOCK_BPS} bps`
                  : hasCurveScenario
                    ? 'Cleared while a curve scenario is set'
                    : 'Parallel shift, e.g. 100 or -100'
              }
            />
          </Stack>
          {isAdcoRun && (
            <Box>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                aria-label="ADCO method"
                value={adcoCshf ? 'cashflow' : 'adco'}
                onChange={(_, method) => method && setAdcoCshf(method === 'cashflow')}
              >
                <ToggleButton value="adco">{adcoMethodLabel(false)}</ToggleButton>
                <ToggleButton value="cashflow">{adcoMethodLabel(true)}</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                {adcoCshf
                  ? 'Derives CPR/CDR/severity from the loans\u2019 summed ADCO cashflow dollars. On the base curve it compares directly against Base, which uses ADCO.'
                  : 'Averages ADCO\u2019s own CPR/CDR/severity across the loans, as Base does. On the base curve it re-runs Base\u2019s ADCO.'}
              </Typography>
            </Box>
          )}
          {!isAdcoRun && (hasShock || hasCurveScenario) && (
            <Typography variant="caption" color="text.secondary">
              ADCO-enabled tranches are re-modeled under this curve when the comparison runs, so their CPR/CDR/severity respond to it too.
            </Typography>
          )}
        </Stack>
      </Box>

      {!isAdcoRun && (
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          aria-label="Comparison run configuration"
          sx={{ px: { xs: 2, md: 3 }, mt: 1.25, minHeight: 42, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<TuneOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label={`Assumptions${overrideCount ? ` (${overrideCount})` : ''}`}
            sx={{ minHeight: 42, textTransform: 'none', fontWeight: 700 }}
          />
          <Tab
            icon={<PriceCheckOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label={`Pricing targets${targetCount ? ` (${targetCount})` : ''}`}
            sx={{ minHeight: 42, textTransform: 'none', fontWeight: 700 }}
          />
        </Tabs>
      )}

      <DialogContent sx={{ px: { xs: 2, md: 3 }, py: 2.5 }}>
        {isAdcoRun ? (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ borderRadius: 2.5, p: 2.5 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>ADCO generation</Typography>
              <Typography variant="body2" color="text.secondary">
                This run generates fresh ADCO assumptions for the selected ADCO-enabled tranches when the comparison starts, using the method chosen above. Each curve scenario or rate shock creates an independently modeled run. Pricing targets below are optional and reprice each tranche&apos;s run.
              </Typography>
            </Paper>
            {pricingTargetsSection}
          </Stack>
        ) : activeTab === 0 && (
          <Stack spacing={2}>
            <Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, color: 'text.primary' }}>
                Deal assumptions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Blank fields use the deal defaults. Highlighted fields are active overrides.
              </Typography>
            </Box>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 750, textTransform: 'uppercase', letterSpacing: 0.65 }}>
                    Optional redemption
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {RUN_TO_CALL_HELP}
                  </Typography>
                </Box>
                <FormControlLabel
                  sx={{ flexShrink: 0, mr: 0 }}
                  control={(
                    <Switch
                      checked={runToCall}
                      onChange={event => handleDealOverrideChange(
                        RUN_TO_CALL_KEY,
                        event.target.checked ? RUN_TO_CALL_VALUE : ''
                      )}
                      inputProps={{ 'aria-label': 'Run to call' }}
                    />
                  )}
                  label="Run to call"
                />
              </Stack>
            </Paper>

            {paramGroups.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Deal assumption fields are unavailable — the field definitions
                could not be loaded from the server.
              </Typography>
            )}
            {paramGroups.map(group => {
              const groupParams = scenarioParams.filter(param => param.group === group);
              const groupOverrideCount = groupParams.filter(param => hasValue(dealOverrides[param.key])).length;
              return (
                <Paper key={group} variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 750, textTransform: 'uppercase', letterSpacing: 0.65 }}>
                      {group}
                    </Typography>
                    {groupOverrideCount > 0 && (
                      <Chip
                        label={`${groupOverrideCount} changed`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '11px' }}
                      />
                    )}
                  </Stack>
                  <Grid container spacing={1.5}>
                    {groupParams.map(param => {
                      const overrideValue = dealOverrides[param.key];
                      const isOverridden = hasValue(overrideValue);
                      const isGeneratedDefault = isAdcoGeneratedVector(
                        param.key,
                        baseValues[param.key],
                        dealData
                      );
                      const defaultLabel = baseValues[param.key]
                        ? `Deal default: ${isGeneratedDefault ? 'ADCO Generated' : baseValues[param.key]}`
                        : 'Use deal default';
                      const fieldId = `comparison-run-${param.key.toLowerCase().replace(/\s+/g, '-')}`;
                      return (
                        <Grid
                          key={param.key}
                          size={{ xs: 12, sm: 6, md: 4 }}
                          sx={{ minWidth: 0 }}
                        >
                          {param.type === 'select' ? (
                            <Tooltip title={param.help || ''} arrow placement="top" enterDelay={500}>
                            <FormControl fullWidth size="small" sx={fieldSx(isOverridden)}>
                              <InputLabel id={`${fieldId}-label`}>{param.label}</InputLabel>
                              <Select
                                id={fieldId}
                                labelId={`${fieldId}-label`}
                                value={overrideValue ?? ''}
                                onChange={event => handleDealOverrideChange(param.key, event.target.value)}
                                label={param.label}
                              >
                                <MenuItem value=""><em>{defaultLabel}</em></MenuItem>
                                {param.choices.map(choice => (
                                  <MenuItem key={choice} value={choice}>{choice}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            </Tooltip>
                          ) : (
                            <Tooltip
                              title={isGeneratedDefault ? baseValues[param.key] : (param.help || '')}
                              arrow
                              placement="top"
                              enterDelay={isGeneratedDefault ? 100 : 500}
                            >
                              <Box>
                                <TextField
                                  fullWidth
                                  label={param.label}
                                  size="small"
                                  placeholder={isGeneratedDefault
                                    ? ''
                                    : (baseValues[param.key] || param.placeholder)}
                                  value={overrideValue ?? ''}
                                  onChange={event => handleDealOverrideChange(param.key, event.target.value)}
                                  InputLabelProps={isGeneratedDefault ? { shrink: true } : undefined}
                                  InputProps={isGeneratedDefault && !isOverridden ? {
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <Box
                                          component="span"
                                          sx={{
                                            color: 'info.main',
                                            fontWeight: 700,
                                            borderBottom: '1px dotted',
                                            cursor: 'help',
                                          }}
                                        >
                                          ADCO Generated
                                        </Box>
                                      </InputAdornment>
                                    ),
                                  } : undefined}
                                  sx={fieldSx(isOverridden)}
                                />
                              </Box>
                            </Tooltip>
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        )}

        {!isAdcoRun && activeTab === 1 && pricingTargetsSection}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 2, justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          <Chip label={curveScenario || 'Base curve'} size="small" color="info" variant="outlined" />
          {!isAdcoRun && (
            <Chip label={`${overrideCount} assumption${overrideCount === 1 ? '' : 's'}`} size="small" variant="outlined" />
          )}
          <Chip label={`${targetCount} pricing target${targetCount === 1 ? '' : 's'}`} size="small" variant="outlined" />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!label.trim() || duplicateLabel || shockOutOfRange}
          >
            {existingRun ? 'Save changes' : `Add ${isAdcoRun ? 'ADCO ' : ''}run`}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
});

ComparisonConfigModal.displayName = 'ComparisonConfigModal';

export default ComparisonConfigModal;
